import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, AlertCircle, Save, Copy, Power, ChevronDown, ChevronRight,
} from 'lucide-react';
import {
  getEvent, updateEvent, toggleEvent, duplicateEvent,
  type EventSummary, type EventUpdateInput,
} from '@/shared/services/eventManagerService';
import { useToast } from '@/shared/contexts/ToastContext';
import { EventTypeBadge, EventStatusBadge, eventTypeDef, toLocalInput } from './eventTypes';
import { ItemNamesProvider } from './GameItem';
import ItemCollectionEditor from './ItemCollectionEditor';
import MonsterDropEditor from './MonsterDropEditor';
import MonsterAttackEditor from './MonsterAttackEditor';

export default function EventDetailPage() {
  const { eventId } = useParams();
  const id = Number(eventId);
  const navigate = useNavigate();
  const { notify } = useToast();
  const [event, setEvent] = useState<EventSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setEvent(await getEvent(id)); }
    catch (err: any) { setError(err?.detail || err?.message || 'Erro'); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const handleToggle = async () => {
    if (!event) return;
    const on = !event.useFlag;
    if (!confirm(`${on ? 'Ativar' : 'Desativar'} este evento?`)) return;
    try { setEvent(await toggleEvent(id, on)); notify('success', on ? 'Ativado' : 'Desativado'); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
  };
  const handleDuplicate = async () => {
    try { const d = await duplicateEvent(id); notify('success', `Duplicado como #${d.eventId}`); navigate(`/admin/events/${d.eventId}`); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
  };

  if (loading) return <div className="py-20 flex justify-center text-white/40"><Loader2 size={22} className="animate-spin" /></div>;
  if (error || !event) return (
    <div className="surface-card rounded-xl p-4 flex items-center gap-3 border-red-500/30">
      <AlertCircle size={18} className="text-red-400" />
      <p className="text-sm text-red-400">{error || 'Evento não encontrado'}</p>
      <button onClick={() => navigate('/admin/events')} className="ml-auto text-xs text-white/60 hover:text-white">← Voltar</button>
    </div>
  );

  const def = eventTypeDef(event.eventType);

  return (
    <ItemNamesProvider>
    <div className="max-w-5xl">
      {/* header */}
      <button onClick={() => navigate('/admin/events')}
        className="flex items-center gap-1.5 text-sm text-white/55 hover:text-white mb-4 transition cursor-pointer">
        <ArrowLeft size={16} /> Eventos
      </button>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-white truncate">{event.name || '(sem nome)'}</h1>
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-xs font-mono text-white/45">#{event.eventId}</span>
            <EventTypeBadge type={event.eventType} />
            <EventStatusBadge status={event.status} />
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={handleDuplicate}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-white/[0.04] text-white/80 hover:bg-white/[0.08] transition cursor-pointer">
            <Copy size={14} /> Duplicar
          </button>
          <button onClick={handleToggle}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition cursor-pointer border
              ${event.useFlag ? 'bg-amber-500/10 text-amber-300 border-amber-500/30 hover:bg-amber-500/20'
                              : 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/20'}`}>
            <Power size={14} /> {event.useFlag ? 'Desativar' : 'Ativar'}
          </button>
        </div>
      </div>

      <EventCommonForm event={event} onSaved={setEvent} />

      <div className="mt-6">
        {event.eventType === 2  && <ItemCollectionEditor event={event} />}
        {event.eventType === 14 && <MonsterDropEditor event={event} />}
        {event.eventType === 11 && <MonsterAttackEditor event={event} />}
        {!def.editor && (
          <div className="surface-card rounded-xl p-8 text-center">
            <p className="text-sm text-white/70 font-semibold">Editor específico ainda não implementado</p>
            <p className="text-xs text-white/45 mt-1">Tipo {event.eventType} — {def.label}. Você pode editar os metadados acima e ativar/desativar.</p>
          </div>
        )}
      </div>
    </div>
    </ItemNamesProvider>
  );
}

/* ------------------------------------------------ Form comum de metadados */
function EventCommonForm({ event, onSaved }: { event: EventSummary; onSaved: (e: EventSummary) => void }) {
  const { notify } = useToast();
  const [f, setF] = useState({
    name: event.name, beginDate: toLocalInput(event.beginDate), endDate: toLocalInput(event.endDate),
    serverIndex: event.serverIndex, participates: event.participates,
    worldIndex: event.worldIndex, npcIndex: event.npcIndex,
    resetDay: event.resetDay, resetHour: event.resetHour, resetMinute: event.resetMinute,
    logMessages: event.logMessages ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [adv, setAdv] = useState(false);
  const [dirty, setDirty] = useState(false);
  const set = (k: keyof typeof f, v: any) => { setF(s => ({ ...s, [k]: v })); setDirty(true); };

  const save = async () => {
    if (!f.name.trim()) { notify('error', 'Nome obrigatório'); return; }
    setBusy(true);
    try {
      const body: EventUpdateInput = {
        name: f.name, beginDate: f.beginDate, endDate: f.endDate, serverIndex: f.serverIndex,
        participates: f.participates, worldIndex: f.worldIndex, npcIndex: f.npcIndex,
        resetDay: f.resetDay, resetHour: f.resetHour, resetMinute: f.resetMinute,
        logMessages: f.logMessages || null,
      };
      onSaved(await updateEvent(event.eventId, body));
      setDirty(false);
      notify('success', 'Metadados salvos');
    } catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
    finally { setBusy(false); }
  };

  return (
    <div className="surface-card rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-bold text-white uppercase tracking-wide">Informações do Evento</h2>
        <button onClick={save} disabled={busy || !dirty}
          className="flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-semibold btn-primary transition cursor-pointer disabled:opacity-40">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Salvar
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <L label="Nome"><input value={f.name} onChange={(e) => set('name', e.target.value)} className={inp} /></L>
        <L label="Server Index"><input value={f.serverIndex} onChange={(e) => set('serverIndex', e.target.value)} className={`${inp} font-mono`} /></L>
        <L label="Início"><input type="datetime-local" value={f.beginDate} onChange={(e) => set('beginDate', e.target.value)} className={inp} /></L>
        <L label="Fim"><input type="datetime-local" value={f.endDate} onChange={(e) => set('endDate', e.target.value)} className={inp} /></L>
      </div>

      <button onClick={() => setAdv(a => !a)} className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white/80 mt-4 transition cursor-pointer">
        {adv ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Configuração avançada
      </button>
      {adv && (
        <div className="grid md:grid-cols-3 gap-3 mt-3 pt-3 border-t border-white/[0.05]">
          <L label="World Index"><input type="number" value={f.worldIndex} onChange={(e) => set('worldIndex', Number(e.target.value))} className={`${inp} font-mono`} /></L>
          <L label="NPC Index"><input type="number" value={f.npcIndex} onChange={(e) => set('npcIndex', Number(e.target.value))} className={`${inp} font-mono`} /></L>
          <L label="Participates"><input type="number" value={f.participates} onChange={(e) => set('participates', Number(e.target.value))} className={`${inp} font-mono`} /></L>
          <L label="Reset Day"><input type="number" value={f.resetDay} onChange={(e) => set('resetDay', Number(e.target.value))} className={`${inp} font-mono`} /></L>
          <L label="Reset Hour"><input type="number" value={f.resetHour} onChange={(e) => set('resetHour', Number(e.target.value))} className={`${inp} font-mono`} /></L>
          <L label="Reset Minute"><input type="number" value={f.resetMinute} onChange={(e) => set('resetMinute', Number(e.target.value))} className={`${inp} font-mono`} /></L>
          <div className="md:col-span-3">
            <L label="Log Messages"><textarea value={f.logMessages} onChange={(e) => set('logMessages', e.target.value)} rows={2} className={`${inp} font-mono resize-y`} /></L>
          </div>
        </div>
      )}
    </div>
  );
}

const inp = 'w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white';
function L({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label className="text-[11px] text-white/55 mb-1 block">{label}</label>{children}</div>;
}
